import React, { useEffect, useState, useContext, useMemo } from "react"
import {Bar} from '../auth/Bar'
import { Grid, Container, Snackbar } from '@mui/material'
import {SideBar} from "../sidebar/SideBar"
import {ActionBar} from "./ActionBar"
import {Board} from '../threejs/Board'
import { LoadScene } from "../threejs/LoadScene";
import { LoadingMessage } from "../threejs/LoadingMessage";
import { Inspector2 } from "../inspector/Inspector2";
import { DialogBox } from "../threejs/DialogBox";
import { SideBarCategory } from "../sidebar/SideBarCategory"
import { useParams, useLocation } from 'react-router-dom'
import { Onboarding } from "../components/Onboarding"
import { useMattoState } from "../MattoState"
import { CollectionModal } from "../studioxr/components/CollectionPopover/CollectionModal"
import { EditLockProvider, useEditLock } from './../projects_v2/edit_lock'; // Adjust path as needed
import { AuthContext } from "../auth/Auth";

const AutoEditLockRequest = ({ permissionCheckComplete, projectId }) => {
  const { requestEdit } = useEditLock();
  const [hasRequested, setHasRequested] = useState(false);

  useEffect(() => {
    if (permissionCheckComplete && !hasRequested && projectId && projectId !== 'new') {
      requestEdit();
      setHasRequested(true);
    }
  }, [permissionCheckComplete, hasRequested, projectId, requestEdit]);

  return null; // This component doesn't render anything
};

export const Project = () => {
	let params = useParams();
	let location = useLocation();
	let props = {...params,location}
	const [isOnline, setIsOnline] = useState(navigator.onLine);
    const OfflineUserSnackMessage = "You are experiencing network problems at the moment"
    const {currentUser} = useContext(AuthContext);

    // Add state to track if LoadScene has finished permission checking
    const [permissionCheckData, setPermissionCheckData] = useState({ complete: false });
    const projectId = params.id;

	useEffect(() => {
		const handleOnline = () => { 
			setIsOnline(true) 
			if (window.gtag)  window.gtag('event','offline detected')
		};
		const handleOffline = () => {  setIsOnline(false)  };

		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);
		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

    const isComplete = permissionCheckData.complete;
    const boardWriteAccess = permissionCheckData.boardData?.writeAccess;
    const boardOwner = permissionCheckData.boardData?.owner;

    const shouldUseLock = useMemo(() => {
        if (!projectId || projectId === 'new' || projectId === 'new_from' || !currentUser) {
            return false;
        }

        // If permission check is complete but no board data, it's a new project
        if (isComplete && !boardWriteAccess) {
            return false;
        }

        // If we have board data, check writeAccess
        if (boardWriteAccess) {
            const hasMultipleUsers = boardWriteAccess && boardWriteAccess.length > 0;
            const hasOtherUsers = boardWriteAccess && boardWriteAccess.some(userId => userId !== boardOwner);
            return hasMultipleUsers && hasOtherUsers;
        }
        return false;
    }, [projectId, currentUser, isComplete, boardOwner, boardWriteAccess]);

    const handlePermissionCheckComplete = (data) => {
        setPermissionCheckData(prev => {
            // If already complete, don't update to avoid re-renders
            if (prev.complete) {
                return prev;
            }
            
            if (data && typeof data === 'object' && data.boardData) {
                return { 
                    complete: true, 
                    boardData: data.boardData 
                };
            } else {
                return { 
                    complete: true 
                };
            }
        });
    }

	const projectContent = (
        <React.Fragment>
            <LoadScene 
                {...props} 
                onPermissionCheckComplete={(data) => handlePermissionCheckComplete(data)}
            />
            <Grid item >
                <Bar />
            </Grid>
            {isComplete && (
            <Container maxWidth={false} style={{paddingLeft:'0px', paddingRight:'10px'}} >
                <Grid spacing={1} container direction="row" justifyContent="center" style={{overflow:'hidden',maxHeight:'92vh'}}>        
                    <Grid item sm={1} md={0.7} xl={0.7} sx={{display: { iphone:'none', sm:'block', md:'block'}, backgroundColor: theme => theme.palette.primary.monochrome.dark }}>
                        <SideBar />
                    </Grid>
                    <Grid item sm={2.5} md={2.4} xl={2} sx={{display: { iphone:'none', sm:'block', md:'block', paddingLeft: '10px' }}}>
                        <SideBarCategory />
                    </Grid>
                    <Grid item iphone={12} sm={6} md={6.5} xl={7.3} id='action_bar' style={{marginTop:'5px', minHeight:'500px'}}>
                        <ActionBar />
                        <Grid item xs={12} style={{height:'84vh'}}>
                            <Board style={{minHeight:'500px'}} {...props} />
                        </Grid>
                    </Grid>
                    <Grid item sm={2.5} md={2.4} xl={2} sx={{display: { iphone:'none', sm:'block', md: 'block' }}}>
                        <Inspector2 />
                    </Grid>
                </Grid>
            </Container>
            )}
            <LoadingMessage />
            <Snackbar 
                id='offlineMessage' 
                autoHideDuration={null} 
                anchorOrigin={{vertical: 'bottom',horizontal: 'center'}} 
                open={!isOnline} 
                message={OfflineUserSnackMessage} 
            />
            <DialogBox/>
            <Onboarding />
            <CollectionModal />
            <GlobalSnackbar />
        </React.Fragment>  
    );

	if (shouldUseLock) {
        return (
            <EditLockProvider
                projectId={projectId}
                currentUserId={currentUser.uid}
                currentUserName={currentUser.displayName || currentUser.email || 'Unknown User'}
                lockTimeoutMs={15 * 60 * 1000} // 2 minutes
                heartbeatIntervalMs={30 * 1000} // 30 seconds
                shouldActivate={isComplete} // Only activate after permission check
            >
                <AutoEditLockRequest 
                    permissionCheckComplete={isComplete}
                    projectId={projectId}
                />
                {projectContent}
            </EditLockProvider>
        );
    }
	return projectContent;

}
export const GlobalSnackbar = () => {
	const snackbarMessage = useMattoState((state) => state.snackbarMessage);
	const snackbarAutoHide = useMattoState((state) => state.snackbarAutoHide);
	const setSnackbarMessage = useMattoState((state) => state.setSnackbarMessage);
	return (
		<Snackbar 
			open={snackbarMessage?.length > 2} 
			onClose={ () => { setSnackbarMessage(null) } } 
			ClickAwayListenerProps={{ onClickAway: () => null }}
			// autoHideDuration={3500}
			autoHideDuration={snackbarAutoHide ? 3500 : null}
			message={snackbarMessage} />
	)
}

